import { WithdrawalId } from '@types';

import { initializeStore } from '@store';
import {
  projectsGetCurrentProjectAdminApiUrl,
  projectsGetCurrentProjectWebApiUrl,
} from '@selectors/projects';

import { AUTH_API_URL, WEB_API_URL } from './env';

const store = initializeStore();
const authApiUrl = AUTH_API_URL;

const webApiUrl = () =>
  projectsGetCurrentProjectWebApiUrl(store.getState()) ?? WEB_API_URL;

const adminApiUrl = () =>
  projectsGetCurrentProjectAdminApiUrl(store.getState());

export const ENDPOINTS = {
  auth: {
    token: (): string => `${authApiUrl}/Auth/login`,
  },

  account: {
    me: (): string => `${webApiUrl()}/me`,
  },

  users: {
    search: (): string => `${webApiUrl()}/users/search`,
    getList: (): string => `${adminApiUrl()}/users/filter`,
    getById: (id: string): string => `${adminApiUrl()}/new/profiles/${id}`,
    getProfileLoginLink: (id: string, email: string): string =>
      `${adminApiUrl()}/users/auth-as?userEmail=${email}&profileId=${id}`,
    getUserInfo: (): string => `${adminApiUrl()}/users/single`,
    updateProfile: (): string => `${adminApiUrl()}/profile`,
    blockLogin: (id: string): string =>
      `${adminApiUrl()}/users/login/lock-unlock/${id}`,
    deactivate: (id: string): string => `${adminApiUrl()}/v2/users/${id}`,
    switchProfile: (): string =>
      `${adminApiUrl()}/users/affiliate/switch-profile`,
    getPhotosByProfileId: (id: string): string =>
      `${webApiUrl()}/profiles/${id}/photos`,
  },

  affiliates: {
    getList: (): string => `${adminApiUrl()}/affiliates`,
    getListWithPaidSearch: (): string => `${adminApiUrl()}/affiliates/filter`,
    getWithdrawalSettings: (): string =>
      `${adminApiUrl()}/affiliates/withdraw-info`,
    lockWithdrawal: (): string => `${adminApiUrl()}/affiliates/lock-withdraw`,
    unlockWithdrawal: (): string =>
      `${adminApiUrl()}/affiliates/unlock-withdraw`,
    updateWhitelistStatus: (): string =>
      `${adminApiUrl()}/affiliates/toggle-whitelist`,
    checkManageAffiliateWithdrawals: (): string =>
      `${adminApiUrl()}/affiliates/current-admin-access`,
    setPaidSearch: (): string => `${adminApiUrl()}/affiliates/paid-search`,
    createSingleAffiliate: (): string => `${webApiUrl()}/accounts/register`,
    createCollectionAffiliates: (): string =>
      `${webApiUrl()}/accounts/register/affiliates`,
    getProfileTransfer: (): string =>
      `${webApiUrl()}/affiliates/profile-transfer/profiles`,
    getProfileTransferProfile: (profileId: string): string =>
      `${webApiUrl()}/affiliates/profile-transfer/profiles/${profileId}`,
    getProfileTransferSchedule: (profileId: string): string =>
      `${webApiUrl()}/affiliates/profile-transfer/profiles/${profileId}/schedule`,
    saveProfileTransfer: (profileId: string): string =>
      `${webApiUrl()}/affiliates/profile-transfer/profiles/${profileId}/schedule`,
    restoreProfile: (profileId: string): string =>
      `${adminApiUrl()}/profile/affiliate/${profileId}`,
    deleteProfile: (profileId: string): string =>
      `${adminApiUrl()}/profile/affiliate/${profileId}`,
    updateOnlineOfflineAccess: (): string =>
      `${webApiUrl()}/affiliates/settings/online-switch`,
  },

  actors: {
    getList: (): string => `${webApiUrl()}/trust/actors`,
    togglePaymentStatus: (actorId: string): string =>
      `${webApiUrl()}/trust/disputes/${actorId}`,
  },

  administrators: {
    getList: (): string => `${adminApiUrl()}/administrators`,
    getPermissions: (): string => `${adminApiUrl()}/administrators/permissions`,
    createSingleAdministrator: (): string =>
      `${webApiUrl()}/administrators/create`,
    getProfile: (profileId: string): string =>
      `${adminApiUrl()}/administrators/${profileId}`,
    updateProfile: (profileId: string): string =>
      `${adminApiUrl()}/administrators/${profileId}`,
  },

  withdrawals: {
    getSystemNotification: (): string =>
      `${adminApiUrl()}/system/notifications/active/1`,
    getAmountsLimits: (): string => `${webApiUrl()}/withdraw/limits`,
    autoConfirm: (): string =>
      `${adminApiUrl()}/payments/withdraw/automatic-state`,
    list: (): string => `${webApiUrl()}/withdraw/orders`,
    orderStatusReject: (orderId: WithdrawalId): string =>
      `${webApiUrl()}/withdraw/orders/${orderId}/reject`,
    orderStatusApprove: (orderId: WithdrawalId): string =>
      `${webApiUrl()}/withdraw/orders/${orderId}/approve`,
    orderHistory: (orderId: WithdrawalId): string =>
      `${webApiUrl()}/withdraw/orders/${orderId}/history`,
    distribution: (): string => `${webApiUrl()}/withdraw/distribution`,
    balance: (id: number): string => `${webApiUrl()}/withdrawals/${id}/balance`,
    limits: (): string => `${webApiUrl()}/withdraw/limits`,
    exchangeRates: (): string => `${webApiUrl()}/withdraw/exchangeRates`,
  },

  payments: {
    distribution: (): string =>
      `${adminApiUrl()}/payments/aggregator/payment-distribution`,
    history: (): string => `${adminApiUrl()}/payments/history`,
    getHistoryById: (id: string): string =>
      `${adminApiUrl()}/payments/history/${id}`,
    updateAttribute: (): string =>
      `${adminApiUrl()}/payments/history/update-attribute`,
    getDatingCurrencyRating: (): string =>
      `${webApiUrl()}/payment/currency/rates`,
  },

  photos: {
    getUnverifiedPhotos: (): string => `${webApiUrl()}/photos/verified`,
    getApprovedPhotos: (): string => `${webApiUrl()}/photos/approved`,
    setApprovedPhotos: (): string => `${webApiUrl()}/photos/approve`,
    deleteById: (id: string): string => `${webApiUrl()}/photos/${id}`,
  },

  reports: {
    getList: (): string => `${webApiUrl()}/perfect-match/results`,
    getCartVerification: (): string =>
      `${webApiUrl()}/payment/credit-cards/verification/summary`,
    getCopilotStatistics: (): string =>
      `${webApiUrl()}/copilot/reports/statistics`,
    getCopilotOptions: (): string => `${webApiUrl()}/copilot/reports/options`,
    getCopilotStatisticsExport: (): string =>
      `${webApiUrl()}/copilot/reports/statistics/export`,
    getCopilotOptionsExport: (): string =>
      `${webApiUrl()}/copilot/reports/options/export`,
  },
};
